import { Scale } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import {
  DEFAULT_DENSITY_INPUT,
  DENSITY_CHALLENGES,
  DENSITY_FIELDS,
  DENSITY_MATCH_TOLERANCE_PERCENT,
  DENSITY_QUESTION,
  REVIEWED_SOLID_DENSITIES,
  calculateDensityTest,
} from './metalsDensityExplore';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';

const BAR_MAX_DENSITY = 20;

/**
 * Density test: typed mass and displaced volume drive the worked ρ = m ÷ V, the comparison with
 * the reviewed solid samples and the density bars from one state.
 */
export function MetalsDensityTest() {
  const [input, setInput] = useState(DEFAULT_DENSITY_INPUT);
  const result = useMemo(() => calculateDensityTest(input), [input]);
  const measuredPercent = Math.min(result.density / BAR_MAX_DENSITY, 1) * 100;

  return (
    <section
      className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6"
      aria-labelledby="metals-density-heading"
    >
      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
        <Scale className="h-4 w-4" aria-hidden="true" /> Density test · Explore
      </p>
      <h2 id="metals-density-heading" className="mt-1 text-xl font-extrabold text-ink">
        Weigh a solid sample and measure the water it displaces
      </h2>
      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <ModelInputPanel legend="Your measurements" fields={DENSITY_FIELDS} values={input} onChange={setInput} />
          <ExploreChallenges question={DENSITY_QUESTION} challenges={DENSITY_CHALLENGES} result={{ input, result }} />
          <WorkedStepsPanel
            title="Density working"
            steps={result.steps}
            stepTestIdPrefix="density-step"
            resultTestId="density-result"
            result={
              result.match
                ? `ρ = ${result.density.toFixed(2)} g/cm³: consistent with ${result.match.name.toLowerCase()}`
                : `ρ = ${result.density.toFixed(2)} g/cm³: no single reviewed match`
            }
            limitNote={result.limitNote}
          />
        </div>
        <figure className="min-w-0 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
          <figcaption className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            Reviewed solid samples (g/cm³) · match within {DENSITY_MATCH_TOLERANCE_PERCENT} %
          </figcaption>
          <ul className="mt-3 space-y-1.5" aria-label="Density comparison">
            {REVIEWED_SOLID_DENSITIES.map((entry) => {
              const candidate = result.candidates.some((item) => item.symbol === entry.symbol);
              return (
                <li key={entry.symbol} className="grid grid-cols-[3.5rem_minmax(0,1fr)_3rem] items-center gap-2 text-xs font-bold text-ink">
                  <span>{entry.name}</span>
                  <span className="relative h-3 rounded-full bg-white">
                    <span
                      className={`absolute inset-y-0 left-0 rounded-full ${candidate ? 'bg-brand-mint' : 'bg-brand-sky/50'}`}
                      style={{ width: `${(entry.density / BAR_MAX_DENSITY) * 100}%` }}
                    />
                  </span>
                  <span className={candidate ? 'font-extrabold' : undefined}>
                    {entry.densityText}
                    {candidate ? ' ✓' : ''}
                  </span>
                </li>
              );
            })}
            <li className="grid grid-cols-[3.5rem_minmax(0,1fr)_3rem] items-center gap-2 border-t border-hairline pt-1.5 text-xs font-extrabold text-ink">
              <span>Yours</span>
              <span className="relative h-3 rounded-full bg-white">
                <span className="absolute inset-y-0 left-0 rounded-full bg-brand-coral" style={{ width: `${measuredPercent}%` }} />
              </span>
              <span>{result.density.toFixed(2)}</span>
            </li>
          </ul>
          {result.density > BAR_MAX_DENSITY ? (
            <p className="mt-2 text-xs font-bold text-slate2">Your value is beyond the 20 g/cm³ end of the bars.</p>
          ) : null}
        </figure>
      </div>
    </section>
  );
}
